import { map } from 'lodash';
import type { ReactElement } from 'react';
import { memo } from 'react';
import { Redirect, Route, Router, Switch } from 'react-router-dom';

import { LandingPage } from 'LandingPage';

import { AuthenticatedRoute } from 'common/auth/auth_route';
import { FeatureFlagsSynchronizer } from 'common/featureFlags/FeatureFlagsSynchronizer';
import { browserHistory } from 'common/routing/browser_history';
import { routes as activeStudySiteRoutes } from 'module/activestudysite/routes';
import { routes as activityRoutes } from 'module/activity/routes';
import { routes as activitybuilderRoutes } from 'module/activitybuilder/routes';
import { routes as activitylibraryRoutes } from 'module/activitylibrary/routes';
import { routes as adminRoutes } from 'module/administration/routes';
import OktaAuthProvider from 'module/auth/okta/OktaAuthProvider';
import { routes as authRoutes } from 'module/auth/routes';
import { routes as customerRoutes } from 'module/customer/routes';
import { routes as dcrRoutes } from 'module/dcrDashboard/routes';
import { routes as dcrManagementRoutes } from 'module/dcrmanagement/routes';
import { routes as devicesRoutes } from 'module/devices/routes';
import { routes as econsentRoutes } from 'module/econsent/routes';
import { routes as econsentbuilderRoutes } from 'module/econsentbuilder/routes';
import { routes as eosRoutes } from 'module/eosdashboard/routes';
import { routes as manageSitesRoutes } from 'module/managesites/routes';
import { routes as reportRoutes } from 'module/mydata/routes';
import { routes as patientRoutes } from 'module/patient/routes';
import { routes as patientAppRoutes } from 'module/patientApp/routes';
import { routes as idRangeRoute } from 'module/patientIdRange/routes';
import { routes as policyRoutes } from 'module/policy/routes';
import { routes as protocolRoutes } from 'module/protocoldesigner/routes';
import { routes as regionRoutes } from 'module/region/routes';
import { routes as siteRoutes } from 'module/site/routes';
import { routes as siteDashboardRoutes } from 'module/sitedashboard/routes';
import { routes as sponsorRoutes } from 'module/sponsor/routes';
import { routes as sponsorDashboardRoutes } from 'module/sponsordashboard/routes';
import { routes as studyRoutes } from 'module/study/routes';
import { routes as studydetailsRoutes } from 'module/studydetails/routes';
import { routes as studyHistoryRoutes } from 'module/studyhistory/routes';
import { routes as studyReportRoutes } from 'module/studyreports/routes';
import { routes as studysitealertRoutes } from 'module/studysitealerts/routes';
import { routes as subscriberRoutes } from 'module/subscriber/routes';
import { routes as userRoutes } from 'module/user/routes';

const configureRoute = (pathPrefix: string, routes: Array<Record<string, any>>) =>
    map(routes, (route) => ({
        ...route,
        path: `${pathPrefix}${route.path}`,
    }));

const routeDefinitions = [].concat(
    configureRoute('', authRoutes),
    configureRoute('', activityRoutes),
    configureRoute('/activitybuilder', activitybuilderRoutes),
    configureRoute('/activitylibrary', activitylibraryRoutes),
    configureRoute('/customer', customerRoutes),
    configureRoute('/site', siteRoutes),
    configureRoute('/sponsor', sponsorRoutes),
    configureRoute('', studyRoutes),
    configureRoute('', patientRoutes),
    configureRoute('/rangeManagement', idRangeRoute),
    configureRoute('', protocolRoutes),
    configureRoute('/econsentbuilder', econsentbuilderRoutes),
    configureRoute('', econsentRoutes),
    configureRoute('/subscriber', subscriberRoutes),
    configureRoute('', userRoutes),
    configureRoute('', adminRoutes),
    configureRoute('', devicesRoutes),
    configureRoute('/policy', policyRoutes),
    configureRoute('', studydetailsRoutes),
    configureRoute('', manageSitesRoutes),
    configureRoute('/siteDashboard', siteDashboardRoutes),
    configureRoute('', reportRoutes),
    configureRoute('', patientAppRoutes),
    configureRoute('', studyReportRoutes),
    configureRoute('', studysitealertRoutes),
    configureRoute('/region', regionRoutes),
    configureRoute('', studyHistoryRoutes),
    configureRoute('', sponsorDashboardRoutes),
    configureRoute('', activeStudySiteRoutes),
    configureRoute('', eosRoutes),
    configureRoute('', dcrRoutes),
    configureRoute('', dcrManagementRoutes)
);

const AppRouter = (): ReactElement => (
    <Router history={browserHistory}>
        <OktaAuthProvider>
            <FeatureFlagsSynchronizer>
                <Switch>
                    <Route path={['/', '/login']} exact>
                        <LandingPage />
                    </Route>
                    {map(routeDefinitions, (route): ReactElement => {
                        if (route.redirects) {
                            return (
                                <Route key={route.path} path={route.path} exact>
                                    <Redirect to={route.redirects} />
                                </Route>
                            );
                        }

                        if (!route.permissions?.length && !route.role?.length) {
                            const AsyncComponent = route.component;

                            return (
                                <Route key={route.path} path={route.path} exact>
                                    <AsyncComponent routes={routeDefinitions} />
                                </Route>
                            );
                        }

                        return (
                            <AuthenticatedRoute
                                key={route.path}
                                path={route.path}
                                exact
                                permissions={route.permissions}
                                role={route.role}
                                component={route.component}
                                accessRoute={route}
                            />
                        );
                    })}
                    <Route>
                        <div>404 Not Found</div>
                    </Route>
                </Switch>
            </FeatureFlagsSynchronizer>
        </OktaAuthProvider>
    </Router>
);

export default memo(AppRouter);
